import React, { FC, useState, useEffect } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import SortableItemComponent from './sortable-item.component';
import * as styles from './sortable.component.module.scss';
import { OptionalChildrenProps } from '../../../types/react.types';

interface SortableComponentProps extends OptionalChildrenProps {
    items: { uuid: string, [key: string]: any }[];
    sortHandlerClass: string;
    classes?: string[];
    orderChanged?: (newOrder: any[]) => void;
    sortEnded: (newOrder: any[]) => void;
}

const SortableComponent: FC<SortableComponentProps> = (props) => {
    const { items, children, sortEnded, classes } = props;
    // We need to map items to their IDs for SortableContext
    const [activeId, setActiveId] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // Wait 5px movement before drag starts to prevent accidental clicks
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    function handleDragStart(event) {
        setActiveId(event.active.id);
    }

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        setActiveId(null);

        if (active.id !== over?.id) {
            const oldIndex = items.findIndex((item) => item.uuid === active.id);
            const newIndex = items.findIndex((item) => item.uuid === over?.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(items, oldIndex, newIndex);
                sortEnded(newOrder);
            }
        }
    }

    // Separate sortable and non-sortable children
    const childArray = React.Children.toArray(children);
    const sortableChildren = childArray.filter(c => !c['props']['data-notsortable']);
    const nonSortableChildren = childArray.filter(c => c['props']['data-notsortable']);

    return (
        <div id="sortable" className={`${(classes || []).join(" ")}`}>
            {nonSortableChildren.map((child, index) => (
                <div key={`non-sortable-${index}`} className={styles['non-sortable-item']} style={{ display: 'inline-block' }}>
                    {child}
                </div>
            ))}

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={items.map(item => item.uuid)}
                    strategy={verticalListSortingStrategy}
                >
                    {sortableChildren.map((child, index) => {
                        // We must find the correct item UUID that matches this child.
                        // Assuming strict 1:1 mapping based on index from the parent props.items
                        const item = items[index];
                        if (!item) return null;

                        return (
                            <SortableItemComponent
                                key={item.uuid}
                                id={item.uuid}
                                index={index}
                            >
                                {child}
                            </SortableItemComponent>
                        );
                    })}
                </SortableContext>
            </DndContext>
        </div>
    );
};

export default SortableComponent;
